import type { FeatureName } from "@lib/featureFlipper";
import type { Permission } from "@lib/permissionConstants";
import sharedConstants from "@lib/shared_constants";

export function resolvePermission(
  granularPermissionsOptedIn: boolean,
  featureOn: (ff: FeatureName) => boolean,
  permission: Permission
) {
  if (granularPermissionsOptedIn) {
    return permission;
  }

  // Some entries in PERMISSION_INFOS have these fields, some don't. Because
  // the type of PERMISSION_INFOS[permission] ends up being a union,
  // TypeScript won't allow trying to access the fields to see if they
  // exist. By casting to this interface, we can indicate to TypeScript
  // that they do optionally exist.
  interface PermissionInfo {
    granular_permission_of?: Permission;
    granular_permission_feature_flippers?: FeatureName[];
  }

  // TypeScript doesn't recognize here that P can index PERMISSION_INFOS.
  // Here we cast it as a key then immediately check that the entry does
  // in fact exist.
  const permissionInfo = sharedConstants.PERMISSION_INFOS[
    permission as keyof typeof sharedConstants.PERMISSION_INFOS
  ] as PermissionInfo | undefined;
  if (!permissionInfo) {
    return permission;
  }

  // If this is not a granular permission then there is no non-granular fallback permission for it
  if (permissionInfo.granular_permission_of == null) {
    return permission;
  }

  // If the permission is granular it needs to be active with all the granular permission feature flippers
  // flipped on. Otherwise we will fall back to the non-granular permission that it's broken out of.
  if ((permissionInfo.granular_permission_feature_flippers ?? []).every((ff: FeatureName) => featureOn(ff))) {
    return permission;
  }

  // if it's a granular permission but not active we do the fallback
  return permissionInfo.granular_permission_of as Permission;
}
